import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { Camera, Save, Lock } from "lucide-react";
import { useStatus } from "../../popup/NotificationContext";
import { setUser } from "../../store/authSlice";
import { useChangeMyPasswordMutation } from "../../redux/admin/userApi";
import { AppDialog } from "../../components/popup";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  CalendarMonthRounded as CalendarMonthRoundedIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  EmailRounded as EmailRoundedIcon,
  ExploreRounded as ExploreRoundedIcon,
  HeightRounded as HeightRoundedIcon,
  InfoOutlined as InfoOutlinedIcon,
  LocalGasStationRounded as LocalGasStationRoundedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PhoneRounded as PhoneRoundedIcon,
  PublicRounded as PublicRoundedIcon,
  SpeedRounded as SpeedRoundedIcon,
  VerifiedRounded as VerifiedRoundedIcon,
  BadgeRounded as BadgeRoundedIcon,
} from "@mui/icons-material";

const TIMEZONE_OPTIONS = ["Asia/Kolkata", "UTC", "Asia/Dubai", "Europe/London", "America/New_York"];
const SPEED_UNIT_OPTIONS = ["km/h", "mph"];
const DISTANCE_UNIT_OPTIONS = [
  { value: "km", label: "km" },
  { value: "mi", label: "miles" },
];
const ALTITUDE_UNIT_OPTIONS = [
  { value: "m", label: "meters (m)" },
  { value: "ft", label: "feet (ft)" },
];
const VOLUME_UNIT_OPTIONS = ["Liter", "Gallon"];

const formatRoleLabel = (value = "") =>
  String(value || "").replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());

const resolveImageUrl = (rawUrl, baseUrl = "") => {
  const value = String(rawUrl || "").trim();
  if (!value || value.startsWith("data:") || /^https?:\/\//i.test(value)) return value;
  return `${baseUrl}${value}`;
};

const fieldShellSx = {
  borderRadius: "18px",
  bgcolor: "rgba(255,255,255,0.92)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
  border: "1px solid rgba(226,232,240,0.92)",
  overflow: "hidden",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    minHeight: 54,
    backgroundColor: "#ffffff",
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
    "& fieldset": {
      borderColor: "rgba(203,213,225,0.95)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(37,99,235,0.45)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2563eb",
      borderWidth: 1.5,
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 700,
    color: "#516175",
  },
};

const iconBubbleSx = {
  width: 48,
  height: 48,
  borderRadius: "16px",
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(180deg, #eef5ff 0%, #f8fbff 100%)",
  color: "#2563eb",
  border: "1px solid rgba(191,219,254,0.85)",
  boxShadow: "0 10px 24px rgba(37,99,235,0.08)",
  flexShrink: 0,
};

const formatDateTime = (value) => {
  if (!value) return "Not available";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Not available";
  }
};

const formatDateOnly = (value) => {
  if (!value) return "Not available";
  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "Not available";
  }
};

const GeneralSetting = () => {
  const dispatch = useDispatch();
  const { showStatus } = useStatus();
  const currentUser = useSelector((state) => state.auth.user);
  const currentRole = String(currentUser?.role || "").trim().toLowerCase();
  const showBarcodeScannerSection = currentRole !== "user";
  const effectiveBarcodeOwner = currentUser?.effectiveBarcodeScannerOwner || null;
  const directBillingOwner = currentUser?.directBillingOwner || null;
  const barcodePaymentVisibilityBlockedBy = currentUser?.barcodePaymentVisibilityBlockedBy || null;
  const isOwnBarcodeActive =
    Boolean(currentUser?.barcodeScannerImage) &&
    String(effectiveBarcodeOwner?._id || "") === String(currentUser?._id || "");
  const [changeMyPassword, { isLoading: isChangingPassword }] = useChangeMyPasswordMutation();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobileNo: "",
    timezone: "Asia/Kolkata",
    speedUnit: "km/h",
    distanceUnit: "km",
    altitudeUnit: "m",
    volumeUnit: "Liter",
    supportContact: "",
    supportEmail: "info@qylmox.com",
    barcodePaymentEnabled: true,
  });
  const [profilePictureDisplayUrl, setProfilePictureDisplayUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [barcodeScannerDisplayUrl, setBarcodeScannerDisplayUrl] = useState("");
  const [selectedBarcodeScannerFile, setSelectedBarcodeScannerFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordSuccess, setChangePasswordSuccess] = useState("");
  const fileInputRef = useRef(null);
  const barcodeScannerInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const placeholderImage = "/profile-placeholder.png";
  const staticFilesBaseUrl =
    backendUrl && backendUrl.endsWith("/api")
      ? backendUrl.slice(0, -4)
      : backendUrl || "";

  useEffect(() => {
    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        username: currentUser.username || "",
        email: currentUser.email || "",
        mobileNo: currentUser.mobileNo || "",
        supportContact: currentUser.mobileNo || prev.supportContact || "",
        supportEmail: currentUser.email || prev.supportEmail || "",
        timezone:
          currentUser?.preferences?.timezone ||
          currentUser?.preferences?.timeZone ||
          prev.timezone,
        speedUnit: currentUser?.preferences?.speedUnit || prev.speedUnit,
        distanceUnit: currentUser?.preferences?.distanceUnit || prev.distanceUnit,
        altitudeUnit: currentUser?.preferences?.altitudeUnit || prev.altitudeUnit,
        volumeUnit: currentUser?.preferences?.volumeUnit || prev.volumeUnit,
        barcodePaymentEnabled: currentUser.barcodePaymentEnabled !== false,
      }));

      const picUrl = currentUser.profilePicture
        ? `${staticFilesBaseUrl}${currentUser.profilePicture}?t=${Date.now()}`
        : `${staticFilesBaseUrl}${placeholderImage}`;
      setProfilePictureDisplayUrl(picUrl);

      const barcodeSource =
        currentUser.barcodeScannerImage || currentUser.effectiveBarcodeScannerImage || "";
      setBarcodeScannerDisplayUrl(
        barcodeSource ? `${resolveImageUrl(barcodeSource, staticFilesBaseUrl)}?t=${Date.now()}` : ""
      );
    }
  }, [currentUser, staticFilesBaseUrl]);

  const profileMeta = useMemo(
    () => [
      {
        label: "User ID",
        value: currentUser?.userId || currentUser?.employeeId || currentUser?._id || "Not available",
        icon: <BadgeRoundedIcon fontSize="small" />,
      },
      {
        label: "Role",
        value: String(currentUser?.role || "User")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase()),
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
      },
      {
        label: "Member Since",
        value: formatDateOnly(currentUser?.createdAt),
        icon: <CalendarMonthRoundedIcon fontSize="small" />,
      },
      {
        label: "Email Verified",
        value:
          currentUser?.emailVerified ||
          currentUser?.isEmailVerified ||
          currentUser?.verified
            ? "Verified"
            : "Not Verified",
        success:
          currentUser?.emailVerified ||
          currentUser?.isEmailVerified ||
          currentUser?.verified,
        icon: <VerifiedRoundedIcon fontSize="small" />,
      },
    ],
    [currentUser]
  );

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return showStatus("error", "Only image files allowed.");
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePictureDisplayUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleBarcodeScannerFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return showStatus("error", "Only image files allowed.");
    }
    setSelectedBarcodeScannerFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setBarcodeScannerDisplayUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const token = localStorage.getItem("token");

      const response = await axios.put(
        `${backendUrl}/users/profile`,
        {
          username: formData.username,
          email: formData.email,
          mobileNo: formData.mobileNo,
          barcodePaymentEnabled: Boolean(formData.barcodePaymentEnabled),
        },
        { headers: { "x-auth-token": token } }
      );

      if (selectedFile) {
        const uploadData = new FormData();
        uploadData.append("profilePicture", selectedFile);

        const picResponse = await axios.post(
          `${backendUrl}/users/upload-profile-picture`,
          uploadData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "x-auth-token": token,
            },
          }
        );

        dispatch(setUser(picResponse.data.user));
      }

      if (selectedBarcodeScannerFile) {
        const barcodeData = new FormData();
        barcodeData.append("barcodeScannerImage", selectedBarcodeScannerFile);
        const barcodeResponse = await axios.post(
          `${backendUrl}/users/profile/barcode-scanner`,
          barcodeData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "x-auth-token": token,
            },
          }
        );
        dispatch(setUser(barcodeResponse.data.user));
      }

      if (!selectedFile && !selectedBarcodeScannerFile) {
        dispatch(setUser(response.data.user));
      }

      showStatus("success", "Profile updated successfully!");
      setSelectedFile(null);
      setSelectedBarcodeScannerFile(null);
    } catch (err) {
      showStatus("error", err.response?.data?.message || "Update failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChangePassword = () => {
    setChangePasswordError("");
    setChangePasswordSuccess("");
    setChangePasswordForm({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setChangePasswordOpen(true);
  };

  const handleSubmitChangePassword = async () => {
    const { oldPassword, newPassword, confirmPassword } = changePasswordForm;
    setChangePasswordError("");
    setChangePasswordSuccess("");

    if (!oldPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setChangePasswordError("All password fields are required.");
      return;
    }
    if (newPassword.trim().length < 6) {
      setChangePasswordError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError("New password and confirm password do not match.");
      return;
    }

    try {
      const response = await changeMyPassword({
        oldPassword: oldPassword.trim(),
        newPassword: newPassword.trim(),
        confirmPassword: confirmPassword.trim(),
      }).unwrap();

      setChangePasswordSuccess(response?.msg || "Password changed successfully.");
      setChangePasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => {
        setChangePasswordOpen(false);
      }, 900);
    } catch (error) {
      setChangePasswordError(
        error?.data?.msg || error?.data?.message || "Failed to change password."
      );
    }
  };

  const renderField = (config) => {
    const commonInputProps = config.icon
      ? {
          startAdornment: (
            <InputAdornment position="start">
              {config.icon}
            </InputAdornment>
          ),
        }
      : undefined;

    return (
      <Grid item xs={12} md={config.md || 4} key={config.name}>
        <Stack spacing={1.1}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#425469" }}>
            {config.label} <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={iconBubbleSx}>{config.leading}</Box>
            {config.type === "select" ? (
              <TextField
                select
                fullWidth
                name={config.name}
                value={formData[config.name]}
                onChange={handleInputChange}
                sx={inputSx}
                SelectProps={{ displayEmpty: true }}
              >
                {config.options.map((option) => {
                  const opt = typeof option === "string" ? { value: option, label: option } : option;
                  return (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  );
                })}
              </TextField>
            ) : (
              <TextField
                fullWidth
                name={config.name}
                value={formData[config.name]}
                onChange={handleInputChange}
                placeholder={config.placeholder}
                sx={inputSx}
                InputProps={{ ...commonInputProps }}
              />
            )}
          </Stack>
        </Stack>
      </Grid>
    );
  };

  const fieldConfigs = [
    {
      name: "username",
      label: "User Name",
      type: "text",
      md: 4,
      leading: <PersonOutlineRoundedIcon fontSize="small" />,
      icon: <PersonOutlineRoundedIcon sx={{ color: "#94a3b8" }} fontSize="small" />,
    },
    {
      name: "timezone",
      label: "Timezone",
      type: "select",
      options: TIMEZONE_OPTIONS,
      md: 4,
      leading: <PublicRoundedIcon fontSize="small" />,
    },
    {
      name: "speedUnit",
      label: "Speed Unit",
      type: "select",
      options: SPEED_UNIT_OPTIONS,
      md: 4,
      leading: <SpeedRoundedIcon fontSize="small" />,
    },
    {
      name: "distanceUnit",
      label: "Distance Unit",
      type: "select",
      options: DISTANCE_UNIT_OPTIONS,
      md: 4,
      leading: <ExploreRoundedIcon fontSize="small" />,
    },
    {
      name: "altitudeUnit",
      label: "Altitude Unit",
      type: "select",
      options: ALTITUDE_UNIT_OPTIONS,
      md: 4,
      leading: <HeightRoundedIcon fontSize="small" />,
    },
    {
      name: "volumeUnit",
      label: "Volume Unit",
      type: "select",
      options: VOLUME_UNIT_OPTIONS,
      md: 4,
      leading: <LocalGasStationRoundedIcon fontSize="small" />,
    },
    {
      name: "mobileNo",
      label: "Support Contact Number",
      type: "text",
      md: 12,
      leading: <PhoneRoundedIcon fontSize="small" />,
      icon: <PhoneRoundedIcon sx={{ color: "#94a3b8" }} fontSize="small" />,
      placeholder: "Enter contact number",
    },
    {
      name: "email",
      label: "Support Email Address",
      type: "text",
      md: 12,
      leading: <EmailRoundedIcon fontSize="small" />,
      icon: <EmailRoundedIcon sx={{ color: "#94a3b8" }} fontSize="small" />,
    },
  ];

  return (
    <>
      <Box component="form" onSubmit={handleUpdateProfile}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "320px minmax(0, 1fr)" },
            gap: { xs: 2.5, xl: 3 },
            alignItems: "start",
          }}
        >
          <Stack spacing={2.5}>
              <Paper elevation={0} sx={{ ...fieldShellSx, p: 0 }}>
                <Box
                  sx={{
                    p: 3,
                    background:
                      "linear-gradient(180deg, #dbeafe 0%, #eff6ff 42%, #ffffff 74%)",
                    borderBottom: "1px solid rgba(226,232,240,0.9)",
                    textAlign: "center",
                  }}
                >
                  <Box sx={{ position: "relative", width: 148, mx: "auto", mb: 2 }}>
                    <Avatar
                      src={profilePictureDisplayUrl}
                      alt="Profile"
                      sx={{
                        width: 148,
                        height: 148,
                        mx: "auto",
                        border: "5px solid #ffffff",
                        boxShadow: "0 20px 40px rgba(37,99,235,0.16)",
                      }}
                    />
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        position: "absolute",
                        right: 6,
                        bottom: 8,
                        width: 42,
                        height: 42,
                        bgcolor: "#ffffff",
                        border: "1px solid rgba(191,219,254,0.9)",
                        color: "#2563eb",
                        boxShadow: "0 12px 24px rgba(37,99,235,0.12)",
                        "&:hover": { bgcolor: "#eff6ff" },
                      }}
                    >
                      <Camera size={18} />
                    </IconButton>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      hidden
                      accept="image/*"
                    />
                  </Box>

                  <Typography sx={{ fontSize: "2rem", fontWeight: 900, color: "#0f172a" }}>
                    {currentUser?.username || "superadmin"}
                  </Typography>
                  <Chip
                    label={String(currentUser?.role || "User")
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                    sx={{
                      mt: 1.2,
                      borderRadius: 999,
                      fontWeight: 800,
                      bgcolor: "rgba(37,99,235,0.12)",
                      color: "#1d4ed8",
                    }}
                  />

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 2.4, color: "#55657a" }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      Last login: {formatDateTime(currentUser?.lastLogin || currentUser?.lastSeen)}
                    </Typography>
                  </Stack>
                </Box>

                <Stack spacing={2.1} sx={{ p: 2.5 }}>
                  {profileMeta.map((item) => (
                    <Stack
                      key={item.label}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Box sx={{ color: "#475569", display: "inline-flex" }}>{item.icon}</Box>
                        <Typography sx={{ color: "#425469", fontWeight: 500 }}>{item.label}</Typography>
                      </Stack>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: item.success ? "#16a34a" : "#0f172a",
                          textAlign: "right",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ ...fieldShellSx, p: 2.5 }}>
                <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a", mb: 2 }}>
                  Contact Information
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <EmailRoundedIcon sx={{ color: "#334155" }} fontSize="small" />
                    <Typography sx={{ color: "#23364c", fontWeight: 500 }}>
                      {formData.email || "Not available"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <PhoneRoundedIcon sx={{ color: "#334155" }} fontSize="small" />
                    <Typography sx={{ color: "#23364c", fontWeight: 500 }}>
                      {formData.mobileNo ? `+91 ${formData.mobileNo}` : "Not available"}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              {showBarcodeScannerSection ? (
                <Paper elevation={0} sx={{ ...fieldShellSx, p: 2.5 }}>
                  <Stack spacing={1.8}>
                    <Box>
                      <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#0f172a" }}>
                        Payment Barcode
                      </Typography>
                      <Typography sx={{ mt: 0.4, color: "#64748b", fontSize: 13 }}>
                        This barcode is shown in the app renewal payment screen.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        minHeight: 160,
                        borderRadius: "16px",
                        border: "1.5px dashed rgba(37,99,235,0.55)",
                        bgcolor: "#f8fbff",
                        display: "grid",
                        placeItems: "center",
                        overflow: "hidden",
                      }}
                    >
                      {barcodeScannerDisplayUrl ? (
                        <Box
                          component="img"
                          src={barcodeScannerDisplayUrl}
                          alt="Payment barcode"
                          sx={{ width: "100%", height: 160, objectFit: "contain", p: 1 }}
                        />
                      ) : (
                        <Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
                          No barcode uploaded
                        </Typography>
                      )}
                      <IconButton
                        onClick={() => barcodeScannerInputRef.current?.click()}
                        aria-label="Upload payment barcode"
                        sx={{
                          position: "absolute",
                          right: 10,
                          bottom: 10,
                          width: 38,
                          height: 38,
                          bgcolor: "#2563eb",
                          color: "#fff",
                          boxShadow: "0 10px 22px rgba(37,99,235,0.24)",
                          "&:hover": { bgcolor: "#1d4ed8" },
                        }}
                      >
                        <Camera size={17} />
                      </IconButton>
                      <input
                        ref={barcodeScannerInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleBarcodeScannerFileChange}
                      />
                    </Box>

                    <FormControlLabel
                      sx={{ m: 0, justifyContent: "space-between" }}
                      labelPlacement="start"
                      control={(
                        <Switch
                          checked={Boolean(formData.barcodePaymentEnabled)}
                          onChange={(event) =>
                            setFormData((prev) => ({
                              ...prev,
                              barcodePaymentEnabled: event.target.checked,
                            }))
                          }
                        />
                      )}
                      label={(
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                            Barcode payment
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                            {formData.barcodePaymentEnabled ? "Enabled" : "Disabled"}
                          </Typography>
                        </Box>
                      )}
                    />

                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                      {barcodePaymentVisibilityBlockedBy?._id ? (
                        <Chip size="small" label={`Hidden by ${barcodePaymentVisibilityBlockedBy.username || "owner"}`} />
                      ) : isOwnBarcodeActive ? (
                        <Chip size="small" color="success" label="Your barcode is active" />
                      ) : effectiveBarcodeOwner?._id ? (
                        <Chip size="small" color="info" label={`Using ${effectiveBarcodeOwner.username || "owner"} barcode`} />
                      ) : (
                        <Chip size="small" color="warning" label="No active barcode" />
                      )}
                      {effectiveBarcodeOwner?.role ? (
                        <Chip size="small" variant="outlined" label={formatRoleLabel(effectiveBarcodeOwner.role)} />
                      ) : null}
                    </Stack>

                    <Typography sx={{ color: "#64748b", fontSize: 11.5, lineHeight: 1.5 }}>
                      {barcodePaymentVisibilityBlockedBy?._id
                        ? `${barcodePaymentVisibilityBlockedBy.username || "Owner"} has disabled barcode payments.`
                        : isOwnBarcodeActive
                          ? "Your barcode will be used for your and child-account renewal payments."
                          : effectiveBarcodeOwner?._id
                            ? `Until you upload your own barcode, ${effectiveBarcodeOwner.username || "owner"}'s barcode is used.`
                            : directBillingOwner?._id
                              ? `Expected billing owner: ${directBillingOwner.username || "owner"}.`
                              : "No effective billing barcode owner is currently available."}
                    </Typography>

                    {selectedBarcodeScannerFile ? (
                      <Typography sx={{ color: "#2563eb", fontSize: 12, fontWeight: 700 }}>
                        Barcode selected. Click Save Changes to upload.
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}
          </Stack>

          <Paper
            elevation={0}
            sx={{
              ...fieldShellSx,
              p: { xs: 2, md: 3.5 },
              minHeight: "100%",
            }}
          >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3, pb: 2.5, borderBottom: "1px solid rgba(226,232,240,0.95)" }}
              >
                <Box>
                  <Typography sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 900, color: "#0f172a" }}>
                    Profile Information
                  </Typography>
                  <Typography sx={{ mt: 0.6, color: "#5d6c7f", fontSize: 16 }}>
                    Update your profile, units & support information
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<Lock size={16} />}
                  onClick={handleOpenChangePassword}
                  sx={{
                    minHeight: 48,
                    px: 2.6,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 800,
                    alignSelf: { xs: "stretch", sm: "center" },
                    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                    boxShadow: "0 14px 30px rgba(37,99,235,0.22)",
                  }}
                >
                  Change Password
                </Button>
              </Stack>

              <Grid container spacing={3}>
                {fieldConfigs.map(renderField)}
              </Grid>

              <Alert
                icon={<InfoOutlinedIcon fontSize="inherit" />}
                severity="info"
                sx={{
                  mt: 3.2,
                  borderRadius: "16px",
                  border: "1px solid rgba(191,219,254,0.9)",
                  bgcolor: "rgba(239,246,255,0.88)",
                  color: "#365375",
                  "& .MuiAlert-icon": { color: "#2563eb" },
                }}
              >
                These preferences will be applied across the entire system.
              </Alert>

              {selectedFile ? (
                <Typography sx={{ mt: 1.4, color: "#2563eb", fontWeight: 600, fontSize: 14 }}>
                  Image selected. Click Save Changes to upload.
                </Typography>
              ) : null}

              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{
                  mt: 4,
                  position: { xs: "sticky", md: "static" },
                  bottom: { xs: 12, md: "auto" },
                  zIndex: 2,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save size={16} />}
                  disabled={isSaving}
                  sx={{
                    minWidth: 188,
                    minHeight: 52,
                    px: 3.2,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 800,
                    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                    boxShadow: "0 14px 30px rgba(37,99,235,0.24)",
                  }}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </Stack>
          </Paper>
        </Box>
      </Box>

      <AppDialog
        open={changePasswordOpen}
        onClose={() => {
          if (!isChangingPassword) setChangePasswordOpen(false);
        }}
        maxWidth="xs"
        title="Change Password"
        loading={isChangingPassword}
        actions={<><Button onClick={() => setChangePasswordOpen(false)} disabled={isChangingPassword} sx={{ textTransform: "none", fontWeight: 700 }}>Cancel</Button><Button variant="contained" onClick={handleSubmitChangePassword} disabled={isChangingPassword} sx={{ borderRadius: "12px", px: 2.5, textTransform: "none", fontWeight: 800 }}>{isChangingPassword ? "Changing..." : "Update Password"}</Button></>}
        PaperProps={{
          sx: {
            borderRadius: "22px",
            p: 1,
          },
        }}
      >
          <Stack spacing={2}>
            {changePasswordError ? <Alert severity="error">{changePasswordError}</Alert> : null}
            {changePasswordSuccess ? <Alert severity="success">{changePasswordSuccess}</Alert> : null}

            <TextField
              label="Current Password"
              type="password"
              fullWidth
              value={changePasswordForm.oldPassword}
              onChange={(event) =>
                setChangePasswordForm((prev) => ({ ...prev, oldPassword: event.target.value }))
              }
              sx={inputSx}
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={changePasswordForm.newPassword}
              onChange={(event) =>
                setChangePasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))
              }
              helperText="Minimum 6 characters"
              sx={inputSx}
            />
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={changePasswordForm.confirmPassword}
              onChange={(event) =>
                setChangePasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
              }
              helperText="Passwords must match"
              sx={inputSx}
            />
          </Stack>
      </AppDialog>
    </>
  );
};

export default GeneralSetting;
